import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Mic, Square, Pause, Play, Trash2, AlertCircle } from 'lucide-react';

// MediaRecorder-Wrapper fuer Phase-B-Recording. Liefert nach Stop einen Blob
// an den Parent (Editor), der dann beim Save zusammen mit der Note hochlaedt.
//
// Bewusst klein: keine Waveform-Visualization, kein Auto-Save. Wenn der Browser
// MediaRecorder nicht supportet (Safari <14.1), wird der Button disabled und
// ein Hinweis gezeigt.

type RecorderState = 'idle' | 'recording' | 'paused' | 'finalized';

export interface AudioRecorderProps {
    /** Wird disabled, wenn kein Patient-Consent vorliegt. */
    disabled?: boolean;
    /** Reason for disabled state, wird als Tooltip + Hinweis angezeigt. */
    disabledReason?: string;
    /** Aufgerufen sobald die Aufnahme fertig ist. Auch beim Verwerfen mit null. */
    onRecordingChange: (blob: Blob | null, durationSec: number) => void;
}

function pickMimeType(): string | null {
    if (typeof MediaRecorder === 'undefined') return null;
    const candidates = [
        'audio/ogg;codecs=opus',
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
    ];
    for (const m of candidates) {
        if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) return m;
    }
    return '';
}

function formatDuration(sec: number): string {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

const AudioRecorder: React.FC<AudioRecorderProps> = ({ disabled = false, disabledReason, onRecordingChange }) => {
    const [state, setState] = useState<RecorderState>('idle');
    const [duration, setDuration] = useState(0);
    const [error, setError] = useState<string | null>(null);

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<BlobPart[]>([]);
    const streamRef = useRef<MediaStream | null>(null);
    const startedAtRef = useRef<number>(0);
    const accumulatedRef = useRef<number>(0);
    const tickRef = useRef<number | null>(null);
    const mimeRef = useRef<string>('');

    const supportsRecording = typeof MediaRecorder !== 'undefined' && typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

    const stopStream = () => {
        const stream = streamRef.current;
        if (stream) {
            stream.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }
    };

    const stopTick = () => {
        if (tickRef.current !== null) {
            window.clearInterval(tickRef.current);
            tickRef.current = null;
        }
    };

    const startTick = () => {
        stopTick();
        startedAtRef.current = Date.now();
        tickRef.current = window.setInterval(() => {
            const live = (Date.now() - startedAtRef.current) / 1000;
            setDuration(accumulatedRef.current + live);
        }, 250);
    };

    useEffect(() => () => {
        stopTick();
        stopStream();
        try { mediaRecorderRef.current?.stop(); } catch { /* ignore */ }
    }, []);

    const handleStart = useCallback(async () => {
        if (disabled || !supportsRecording) return;
        setError(null);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const mime = pickMimeType();
            mimeRef.current = mime || '';
            const recorder = mime
                ? new MediaRecorder(stream, { mimeType: mime })
                : new MediaRecorder(stream);
            mediaRecorderRef.current = recorder;
            chunksRef.current = [];

            recorder.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
            };
            recorder.onstop = () => {
                stopTick();
                const totalSec = accumulatedRef.current + (Date.now() - startedAtRef.current) / 1000;
                const blob = new Blob(chunksRef.current, { type: mimeRef.current || 'audio/webm' });
                stopStream();
                setState('finalized');
                onRecordingChange(blob, Math.round(totalSec));
            };

            recorder.start(1000); // 1s timeslice — robust gegen abrupt-stop
            accumulatedRef.current = 0;
            setDuration(0);
            startTick();
            setState('recording');
        } catch (err: any) {
            setError(err?.message || 'Mikrofon-Zugriff verweigert');
            setState('idle');
            stopStream();
        }
    }, [disabled, supportsRecording, onRecordingChange]);

    const handlePause = () => {
        const r = mediaRecorderRef.current;
        if (!r || state !== 'recording') return;
        r.pause();
        accumulatedRef.current += (Date.now() - startedAtRef.current) / 1000;
        stopTick();
        setState('paused');
    };

    const handleResume = () => {
        const r = mediaRecorderRef.current;
        if (!r || state !== 'paused') return;
        r.resume();
        startTick();
        setState('recording');
    };

    const handleStop = () => {
        const r = mediaRecorderRef.current;
        if (!r || (state !== 'recording' && state !== 'paused')) return;
        try { r.stop(); } catch { /* ignore */ }
    };

    const handleDiscard = () => {
        chunksRef.current = [];
        accumulatedRef.current = 0;
        setDuration(0);
        setState('idle');
        onRecordingChange(null, 0);
    };

    if (!supportsRecording) {
        return (
            <div className="flex items-center gap-2 text-xs text-brand-text-muted">
                <AlertCircle className="w-3.5 h-3.5" />
                Browser unterstützt keine Audio-Aufnahme.
            </div>
        );
    }

    const isActive = state === 'recording' || state === 'paused';
    const tooltip = disabled ? (disabledReason || 'Aufnahme deaktiviert') : undefined;

    return (
        <div className="flex items-center gap-2" title={tooltip}>
            {state === 'idle' && (
                <button
                    type="button"
                    onClick={handleStart}
                    disabled={disabled}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer text-brand-primary border-brand-primary/40 hover:bg-brand-primary/5"
                >
                    <Mic className="w-3.5 h-3.5" />
                    Aufnahme
                </button>
            )}

            {state === 'recording' && (
                <>
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-red-600">
                        <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" aria-hidden />
                        <span className="tabular-nums">{formatDuration(duration)}</span>
                    </span>
                    <button
                        type="button"
                        onClick={handlePause}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-brand-text-muted hover:text-brand-text border border-brand-border rounded-md hover:bg-brand-surface-2 cursor-pointer transition-colors"
                        title="Pause"
                    >
                        <Pause className="w-3.5 h-3.5" />
                    </button>
                    <button
                        type="button"
                        onClick={handleStop}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-md cursor-pointer transition-colors"
                        title="Stopp"
                    >
                        <Square className="w-3.5 h-3.5" fill="white" />
                    </button>
                </>
            )}

            {state === 'paused' && (
                <>
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text-muted">
                        <span className="w-2 h-2 rounded-full bg-brand-text-muted" aria-hidden />
                        <span className="tabular-nums">{formatDuration(duration)}</span>
                        <span className="opacity-60">· pausiert</span>
                    </span>
                    <button
                        type="button"
                        onClick={handleResume}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-brand-primary border border-brand-primary/40 rounded-md hover:bg-brand-primary/5 cursor-pointer transition-colors"
                        title="Fortsetzen"
                    >
                        <Play className="w-3.5 h-3.5" />
                    </button>
                    <button
                        type="button"
                        onClick={handleStop}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-md cursor-pointer transition-colors"
                        title="Stopp"
                    >
                        <Square className="w-3.5 h-3.5" fill="white" />
                    </button>
                </>
            )}

            {state === 'finalized' && (
                <>
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-primary">
                        <Mic className="w-3.5 h-3.5" />
                        Aufnahme bereit ({formatDuration(duration)})
                    </span>
                    <button
                        type="button"
                        onClick={handleDiscard}
                        className="inline-flex items-center gap-1 p-1.5 text-xs text-brand-text-muted hover:text-red-600 hover:bg-red-50 rounded-md cursor-pointer transition-colors"
                        title="Verwerfen"
                    >
                        <Trash2 className="w-3.5 h-3.5" />
                    </button>
                </>
            )}

            {error && (
                <span className="inline-flex items-center gap-1 text-xs text-red-600">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {error}
                </span>
            )}
        </div>
    );
};

export default AudioRecorder;
